import { COLOR_PRESETS } from '@/hooks/useStoreSettings';
import { RADIUS_OPTIONS, BUTTON_STYLES, CARD_STYLES } from './designConstants';
import { DesignSettings, SectionSaveState } from './designTypes';
import { SaveButton, ToggleRow } from './DesignShared';

interface Props {
  design: DesignSettings;
  updateDesign: (patch: Partial<DesignSettings>) => void;
  componentsSaveState: SectionSaveState;
  onSaveComponents: () => void;
  selectedColor: typeof COLOR_PRESETS[0];
  selectedRadius: typeof RADIUS_OPTIONS[0];
}

export default function ComponentsSection({
  design, updateDesign,
  componentsSaveState, onSaveComponents,
  selectedColor, selectedRadius,
}: Props) {
  return (
    <div className="flex flex-col gap-5">
      {/* Border Radius */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center bg-[#1a1a1a] rounded-lg">
            <i className="ri-rounded-corner text-white text-[10px]"></i>
          </div>
          حواف الزوايا
        </h3>
        <p className="text-xs text-gray-400 mb-4">شكل زوايا البطاقات والأزرار</p>
        <div className="grid grid-cols-5 gap-3">
          {RADIUS_OPTIONS.map(opt => (
            <button
              key={opt.id}
              onClick={() => updateDesign({ radiusId: opt.id })}
              className={`flex flex-col items-center gap-2 p-3 transition-all cursor-pointer ${design.radiusId === opt.id ? 'opacity-100' : 'opacity-60 hover:opacity-80'}`}
            >
              <div
                className="w-10 h-10 border-2 border-[#1a1a1a] bg-white transition-all"
                style={{ borderRadius: opt.value, boxShadow: design.radiusId === opt.id ? '0 0 0 3px rgba(0,0,0,0.15)' : 'none' }}
              ></div>
              <span className="text-[10px] text-gray-500 font-medium whitespace-nowrap">{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Button Style */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center bg-amber-400 rounded-lg">
            <i className="ri-cursor-line text-black text-[10px]"></i>
          </div>
          نمط الأزرار
        </h3>
        <p className="text-xs text-gray-400 mb-4">شكل الأزرار الرئيسية في الموقع</p>
        <div className="grid grid-cols-3 gap-3">
          {BUTTON_STYLES.map(style => (
            <button
              key={style.id}
              onClick={() => updateDesign({ buttonStyleId: style.id })}
              className={`flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                design.buttonStyleId === style.id ? 'border-[#1a1a1a] bg-white' : 'border-gray-100 bg-white hover:border-gray-300'
              }`}
            >
              <div
                className="px-4 py-2 text-xs font-bold whitespace-nowrap"
                style={{
                  borderRadius: selectedRadius.value,
                  ...(style.id === 'filled'
                    ? { background: selectedColor.primary, color: selectedColor.textOnPrimary }
                    : style.id === 'outlined'
                    ? { border: `2px solid ${selectedColor.primary}`, color: selectedColor.primary, background: 'transparent' }
                    : { background: `${selectedColor.primary}20`, color: selectedColor.primary }),
                }}
              >
                أضف للسلة
              </div>
              <div className="text-center">
                <p className="text-xs font-bold text-[#1a1a1a]">{style.label}</p>
                <p className="text-[10px] text-gray-400">{style.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Card Style */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-1 flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center bg-[#1a1a1a] rounded-lg">
            <i className="ri-layout-grid-line text-white text-[10px]"></i>
          </div>
          نمط البطاقات
        </h3>
        <p className="text-xs text-gray-400 mb-4">مظهر بطاقات المنتجات</p>
        <div className="grid grid-cols-2 gap-3">
          {CARD_STYLES.map(style => (
            <button
              key={style.id}
              onClick={() => updateDesign({ cardStyleId: style.id })}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer text-right ${
                design.cardStyleId === style.id ? 'border-[#1a1a1a] bg-white' : 'border-gray-100 bg-white hover:border-gray-300'
              }`}
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${design.cardStyleId === style.id ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}>
                <i className={`${style.icon} text-sm ${design.cardStyleId === style.id ? 'text-white' : 'text-gray-500'}`}></i>
              </div>
              <span className="text-xs font-bold text-[#1a1a1a]">{style.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Animations */}
      <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
        <ToggleRow
          icon="ri-magic-line" iconBg="bg-[#1a1a1a] text-white"
          label="الحركات والانتقالات" desc="تفعيل تأثيرات الحركة في الموقع"
          value={design.animationsEnabled} onChange={v => updateDesign({ animationsEnabled: v })}
        />
      </div>

      <SaveButton state={componentsSaveState} onClick={onSaveComponents} label="حفظ مكونات التصميم" />
    </div>
  );
}
